import { useState, useEffect, useRef, useMemo } from "react"

// Algolia (same instance + index the New Quote / CMS quoting searches use)
import algoliasearch from "algoliasearch/lite"
import { InstantSearch, connectSearchBox, connectHits, connectStateResults } from "react-instantsearch-dom"

// Icons
import { Search as SearchGlyph, Plus } from "lucide-react"

// UI components
import Input from "../../../components/ui/inputs/input"
import Badge from "../../../components/ui/badge/badge"

// ---------------------------------------------------------------------------
// Algolia-backed hotel search for the flow builder - a search box with an
// inline floating result list, mirroring the CMS "Quoting" tab picker. Hotels
// already in the flow are filtered out so the same one can't be picked twice.
// `onType` surfaces the live query so the diagram can sketch a ghost node
// while the agent is still typing. Connected widgets live at module scope and
// receive their per-instance config as plain own-props.
// ---------------------------------------------------------------------------
const SearchInput = connectSearchBox(({ currentRefinement, refine, placeholder, onQuery, autoFocus }) => (
	<form noValidate role="search" onSubmit={(event) => event.preventDefault()}>
		<Input
			value={currentRefinement}
			icon={<SearchGlyph size={16} />}
			fullEvent={true}
			placeholder={placeholder}
			id={autoFocus ? "qa-autofocus-search" : undefined}
			onChange={(event) => {
				refine(event.currentTarget.value)
				onQuery(event.currentTarget.value)
			}}
		/>
	</form>
))

const PickerHits = connectHits(({ hits, excludeIds, onPick }) => {
	const visible = hits.filter((hit) => !excludeIds.includes(hit.objectID)).slice(0, 8)

	if (!visible.length) {
		return <div className="qa-picker-none">No hotels found</div>
	}

	return (
		<div className="qa-picker-group">
			{visible.map((hit) => (
				<div key={hit.objectID} className="qa-picker-result" onClick={() => onPick(hit)}>
					<Badge label="Hotel" type="INFO" />
					<p>{hit.title}</p>
					<Plus size={14} />
				</div>
			))}
		</div>
	)
})

// Only render hits once something has been typed
const PickerResults = connectStateResults(({ searchState, excludeIds, onPick }) =>
	searchState.query ? <PickerHits excludeIds={excludeIds} onPick={onPick} /> : null
)

export default function HotelPicker({ placeholder, excludeIds, onPick, onType, autoFocus }) {
	const [open, setOpen] = useState(false)

	// Bumping the generation remounts InstantSearch, clearing the query after a
	// pick so the picker is ready for the next hotel straight away.
	const [generation, setGeneration] = useState(0)
	const wrapperRef = useRef(null)

	const searchClient = useMemo(() => algoliasearch("HY764WYKNV", "059a9cd79a33ec2b2822acf2ffdfd4c9"), [])

	// Close the dropdown when clicking outside the picker
	useEffect(() => {
		const handleClick = (event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false)
		}
		document.addEventListener("mousedown", handleClick)
		return () => document.removeEventListener("mousedown", handleClick)
	}, [])

	// Pull focus into the search field when asked to (e.g. a brand new flow)
	useEffect(() => {
		if (autoFocus) document.getElementById("qa-autofocus-search")?.focus()
	}, [autoFocus, generation])

	// Surface the in-flight query for the diagram's ghost node
	const handleQuery = (query) => {
		setOpen(true)
		onType && onType(query)
	}

	// Hand the hit up, then reset the search ready for the next pick
	const handlePick = (hit) => {
		onPick(hit)
		onType && onType("")
		setGeneration((generation) => generation + 1)
	}

	return (
		<div
			className="qa-picker"
			ref={wrapperRef}
			onFocus={() => setOpen(true)}
			onKeyDown={(event) => event.key === "Escape" && setOpen(false)}>
			<InstantSearch key={generation} searchClient={searchClient} indexName="cms_holidays">
				<SearchInput placeholder={placeholder} onQuery={handleQuery} autoFocus={autoFocus} />
				{open && (
					<div className="qa-picker-results">
						<PickerResults excludeIds={excludeIds} onPick={handlePick} />
					</div>
				)}
			</InstantSearch>
		</div>
	)
}
